import { useEffect, useState } from "react"
import { adminApi, type DashboardStats } from "@/lib/admin-api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
    Package, FolderTree, ShoppingCart, TrendingUp,
    Clock, CheckCircle, Truck, AlertTriangle,
    ArrowUpRight, RefreshCw
} from "lucide-react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

export default function AdminDashboard() {
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const fetchStats = async () => {
        try {
            setIsLoading(true)
            const data = await adminApi.getDashboardStats()
            setStats(data)
        } catch (error) {
            console.error("Failed to fetch stats:", error)
            toast.error("Statistikani yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchStats()
    }, [])

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat("uz-UZ").format(price) + " so'm"
    }

    const statCards = [
        {
            title: "Mahsulotlar",
            value: stats?.products_count || 0,
            icon: Package,
            gradient: "from-blue-500 to-blue-600",
            link: "/admin/products"
        },
        {
            title: "Kategoriyalar",
            value: stats?.categories_count || 0,
            icon: FolderTree,
            gradient: "from-purple-500 to-purple-600",
            link: "/admin/categories"
        },
        {
            title: "Buyurtmalar",
            value: stats?.orders_count || 0,
            icon: ShoppingCart,
            gradient: "from-emerald-500 to-teal-600",
            link: "/admin/orders"
        },
        {
            title: "Daromad",
            value: formatPrice(stats?.total_revenue || 0),
            icon: TrendingUp,
            gradient: "from-amber-500 to-orange-600",
            link: "/admin/orders"
        },
    ]

    const orderStats = [
        { label: "Kutilmoqda", value: stats?.pending_orders || 0, icon: Clock, color: "text-amber-500" },
        { label: "Tasdiqlangan", value: stats?.confirmed_orders || 0, icon: CheckCircle, color: "text-blue-500" },
        { label: "Yetkazilgan", value: stats?.delivered_orders || 0, icon: Truck, color: "text-emerald-500" },
    ]

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yuklanmoqda...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
                    <p className="text-muted-foreground">Sayt statistikasi va oxirgi faoliyat</p>
                </div>
                <Button variant="outline" onClick={fetchStats} className="gap-2">
                    <RefreshCw className="h-4 w-4" />
                    Yangilash
                </Button>
            </div>

            {/* Main Stats */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {statCards.map((stat) => (
                    <Link key={stat.title} to={stat.link}>
                        <Card className="relative overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
                            <div className={cn(
                                "absolute inset-0 bg-gradient-to-br opacity-5 group-hover:opacity-10 transition-opacity",
                                stat.gradient
                            )} />
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">{stat.title}</p>
                                        <p className="text-3xl font-bold mt-1">{stat.value}</p>
                                    </div>
                                    <div className={cn(
                                        "w-14 h-14 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white",
                                        stat.gradient
                                    )}>
                                        <stat.icon className="h-7 w-7" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 mt-4 text-sm text-emerald-600">
                                    <ArrowUpRight className="h-4 w-4" />
                                    <span>Batafsil</span>
                                </div>
                            </CardContent>
                        </Card>
                    </Link>
                ))}
            </div>

            {/* Order Stats */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        Buyurtmalar holati
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-3 gap-6">
                        {orderStats.map((stat) => (
                            <div key={stat.label} className="text-center p-4 bg-slate-50 dark:bg-gray-800 rounded-xl">
                                <stat.icon className={cn("h-8 w-8 mx-auto mb-2", stat.color)} />
                                <p className="text-2xl font-bold">{stat.value}</p>
                                <p className="text-sm text-muted-foreground">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Orders */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Oxirgi buyurtmalar</CardTitle>
                        <Link to="/admin/orders">
                            <Button variant="ghost" size="sm">
                                Barchasi
                                <ArrowUpRight className="h-4 w-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {stats?.recent_orders?.slice(0, 5).map((order) => (
                                <div key={order.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-gray-800 rounded-lg">
                                    <div>
                                        <p className="font-medium">{order.customer_name}</p>
                                        <p className="text-sm text-muted-foreground">{order.product_name}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold">{formatPrice(Number(order.total_price))}</p>
                                        <Badge variant={
                                            order.status === "pending" ? "secondary" :
                                                order.status === "confirmed" ? "default" :
                                                    order.status === "delivered" ? "default" : "destructive"
                                        } className={cn(
                                            order.status === "delivered" && "bg-emerald-500"
                                        )}>
                                            {order.status === "pending" ? "Kutilmoqda" :
                                                order.status === "confirmed" ? "Tasdiqlangan" :
                                                    order.status === "delivered" ? "Yetkazilgan" : "Bekor"}
                                        </Badge>
                                    </div>
                                </div>
                            ))}
                            {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                                <p className="text-center text-muted-foreground py-8">Buyurtmalar yo'q</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Low Stock Products */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Kam qolgan mahsulotlar
                        </CardTitle>
                        <Link to="/admin/products">
                            <Button variant="ghost" size="sm">
                                Barchasi
                                <ArrowUpRight className="h-4 w-4 ml-1" />
                            </Button>
                        </Link>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {stats?.low_stock_products?.slice(0, 5).map((product) => (
                                <div key={product.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-gray-800 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        {product.image_url ? (
                                            <img src={product.image_url} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-gray-700 flex items-center justify-center">
                                                <Package className="h-5 w-5 text-muted-foreground" />
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-medium">{product.name}</p>
                                            <p className="text-sm text-muted-foreground">{product.category_name}</p>
                                        </div>
                                    </div>
                                    <Badge variant="destructive" className="bg-amber-500">
                                        {product.stock} ta qoldi
                                    </Badge>
                                </div>
                            ))}
                            {(!stats?.low_stock_products || stats.low_stock_products.length === 0) && (
                                <p className="text-center text-muted-foreground py-8">Barcha mahsulotlar yetarli</p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
